import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import reducer from './reducer';

const initialState = {
    page: 0,
    rowsPerPage: 25,
    filterString: '',
    sort: 'LastName Asc',
    recOffset: 0,
    searchValue: '',
    searchKeys: ['LastName', 'FirstName', 'Email'],
    searchString: '',
    filterObjectString: '',
    sortKey: 'LastName',
    sortDir: 'Asc',
    isFetching: true,
    filters: {},
    totalRecordCount: 0,
    totalUnfilteredCount: 0,
    currentCount: 0,
    headers: {},
    list: [],
    hasMore: true,
    isInitialized: false,
};

const useRequestBuilder = (savedFilterState) => {
    const [requestObj, dispatch] = React.useReducer(reducer, savedFilterState ? savedFilterState : initialState);
    const searchHelperText = `Search for application(s) by ${requestObj.searchKeys.slice(0, 2).join(', ')}, etc`;
    const history = useHistory();

    const handleFilterString = (newVal, type) => {
        let newFilterString = '',
            newSearchString = '',
            newTotalString = '',
            newState = {};

        switch (type) {
            case 'search':
                if (newVal !== '') {
                    newSearchString = requestObj?.searchKeys.map((key) => `${key} LIKE '%${newVal}%'`).join(' OR ');

                    newTotalString =
                        requestObj?.filterObjectString !== ''
                            ? `${requestObj.filterObjectString} AND (${newSearchString})`
                            : newSearchString;
                } else {
                    newTotalString = requestObj?.filterObjectString;
                }

                newState.filterString = newTotalString;
                newState.searchString = newSearchString;
                newState.searchValue = newVal;
                break;
            case 'filter':
                let selectedFilterEntries = Object.entries(newVal);
                newFilterString = selectedFilterEntries
                    .map(([key, filters]) => {
                        return filters?.length > 0 ? `${key} IN (${filters.join(', ')})` : '';
                    })
                    .filter((str) => str !== '')
                    .join(' OR ');

                if (newFilterString !== '') {
                    newTotalString =
                        requestObj?.searchString !== '' ? `${newFilterString} AND (${requestObj?.searchString})` : newFilterString;
                } else {
                    newTotalString = requestObj?.searchString !== '' ? requestObj?.searchString : '';
                }

                newState.filterString = newTotalString;
                newState.filterObjectString = newFilterString;
                newState.selectedFilters = newVal;
                break;
            default:
                return;
        }

        if (newState.filterString !== requestObj.filterString) {
            dispatch({ type: 'filter', newState });
            retrieveList(newState.filterString, requestObj.sort, 0);
        }
    };

    const handleSort = (key) => {
        let newState = {};

        if (key !== requestObj.sortKey) {
            newState.sortKey = key;
            newState.sortDir = 'Asc';

            newState.sort = `${key} Asc`;
        } else {
            newState.sortDir = requestObj?.sortDir === 'Asc' ? 'Desc' : 'Asc';

            newState.sort = `${key} ${newState.sortDir}`;
        }

        dispatch({ type: 'sort', newState });

        retrieveList(requestObj.filterString, newState.sort, 0);
    };

    const retrieveList = async (filterString, sort, recOffset) => {
        let requestBody = {
            FilterString: filterString !== undefined ? filterString : requestObj.filterString,
            Sort: sort !== undefined ? sort : requestObj.sort,
            RecOffset: recOffset !== undefined ? recOffset : requestObj.recOffset,
        };

        dispatch({ type: 'fetching' });

        try {
            let resp = await API.post('ShipyardAPI', '/admin/dashboard', {
                body: requestBody,
            });

            let newList = requestBody.RecOffset === 0 ? resp.list : [...requestObj.list, ...resp.list];

            let newState = {
                ...resp,
                list: newList,
                currentCount: resp.list.length,
                totalRecordCount: Number.isInteger(resp.list?.[0]?.TotalRecordCount) ? resp.list[0].TotalRecordCount : 0,
                hasMore: newList.length < resp?.list?.[0]?.TotalRecordCount,
                isInitialized: true,
                recOffset: requestBody.RecOffset + 25,
            };

            if(requestObj?.isInitialized === false) newState.totalUnfilteredCount = resp?.list?.[0]?.TotalRecordCount || 0;

            dispatch({ type: 'update', newState });
        } catch (error) {
            toast.error('Error occurred');
        } finally {
            dispatch({ type: 'fetched' });
        }
    };

    const loadMore = async () => {
        await retrieveList();
    };

    const handleNav = async (href) => {
        history.push(href, requestObj);
    };

    const exportApplications = async () => {
        let id = toast.loading('Exporting applications...');
        dispatch({ type: 'fetching' });
        let successful = false;

        try {
            let { downloadURL } = await API.post('ShipyardAPI', '/admin/download_report');

            toast.dismiss(id);
            toast.success('Report downloaded.');
            dispatch({ type: 'update', newState: { reportDownloadURL: downloadURL } });
            successful = true;
        } catch (e) {
            toast.dismiss(id);
            toast.error('API error.');
        } finally {
            dispatch({ type: 'fetched' });
        }

        return successful;
    };

    const handlePageChange = (event, newPage) => {
        dispatch({
            type: 'update',
            newState: {
                page: newPage,
            },
        });
    };

    const handleRowsPerPageChange = (event) => {
        dispatch({
            type: 'update',
            newState: {
                page: 0,
                rowsPerPage: event.target.value,
            },
        });
    };

    return {
        ...requestObj,
        searchHelperText,
        handleFilterString,
        handleSort,
        handleNav,
        handlePageChange,
        handleRowsPerPageChange,
        retrieveList,
        loadMore,
        exportApplications,
    };
};

export default useRequestBuilder;
